import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Baby, Clock, Star, AlertTriangle, Sparkles, ChefHat } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BabyModePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ageMonths, setAgeMonths] = useState('12');
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adaptDialog, setAdaptDialog] = useState({ open: false, recipe: null });
  const [adaptedRecipe, setAdaptedRecipe] = useState(null);
  const [adapting, setAdapting] = useState(false);

  useEffect(() => {
    fetchBabyRecipes();
  }, [ageMonths]);

  const fetchBabyRecipes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/recipes/baby-friendly?age_months=${ageMonths}&limit=20`, { withCredentials: true });
      setRecipes(response.data.recipes || []);
    } catch (error) {
      console.error('Failed to fetch baby recipes:', error);
      // Fallback to regular recipes
      const fallback = await axios.get(`${API}/recipes?limit=12`, { withCredentials: true });
      setRecipes(fallback.data.recipes || []);
    } finally {
      setLoading(false);
    }
  };

  const handleAdaptRecipe = async (recipe) => {
    setAdaptDialog({ open: true, recipe });
    setAdapting(true);
    setAdaptedRecipe(null);
    
    try {
      const response = await axios.post(`${API}/recipes/${recipe.recipe_id}/baby-adapt`, {
        age_months: parseInt(ageMonths)
      }, { withCredentials: true });
      setAdaptedRecipe(response.data.adapted_recipe);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setAdapting(false);
    }
  };

  const getAgeLabel = (months) => {
    if (months < 6) return t('baby.age_0_6');
    if (months < 12) return t('baby.age_6_12');
    if (months < 24) return t('baby.age_12_24');
    return t('baby.age_24_plus');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4 max-w-6xl">
        <div className="flex items-center gap-3 mb-4 sm:mb-8">
          <div className="bg-pink-500 rounded-full p-2 sm:p-3">
            <Baby className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="baby-mode-title">
              {t('baby.title')}
            </h1>
            <p className="text-sm sm:text-base text-gray-600">{t('baby.subtitle')}</p>
          </div>
        </div>

        {/* Age Selector */}
        <Card className="mb-4 sm:mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">{t('baby.select_age')}</label>
                <Select value={ageMonths} onValueChange={setAgeMonths}>
                  <SelectTrigger className="w-full md:w-64" data-testid="age-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">{t('baby.age_6_12')}</SelectItem>
                    <SelectItem value="12">{t('baby.age_12_24')}</SelectItem>
                    <SelectItem value="24">{t('baby.age_24_plus')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">{t('baby.disclaimer')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Safety Tips */}
        <Card className="mb-8 bg-pink-50 border-pink-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-pink-700">
              <Baby className="w-5 h-5" />
              {t('baby.safety_tips_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-pink-800">
              <li>• {t('baby.tip_1')}</li>
              <li>• {t('baby.tip_2')}</li>
              <li>• {t('baby.tip_3')}</li>
              <li>• {t('baby.tip_4')}</li>
            </ul>
          </CardContent>
        </Card>

        {/* Recipes Grid */}
        <h2 className="text-2xl font-heading font-bold text-[#2C3E2D] mb-4">{t('baby.suitable_recipes')}</h2>
        
        {recipes.length === 0 ? (
          <div className="text-center py-12">
            <Baby className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">{t('baby.no_recipes')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {recipes.map((recipe) => (
              <Card key={recipe.recipe_id} className="hover:shadow-lg transition-shadow" data-testid={`baby-recipe-${recipe.recipe_id}`}>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-heading font-bold mb-2">{recipe.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{recipe.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {recipe.cook_time_min} {t('common.min')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      {recipe.ratings_avg?.toFixed(1) || '0.0'}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/recipe/${recipe.recipe_id}`)}
                    >
                      <ChefHat className="w-4 h-4 mr-1" />
                      {t('baby.view_recipe')}
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 bg-pink-500 hover:bg-pink-600"
                      onClick={() => handleAdaptRecipe(recipe)}
                      data-testid={`adapt-recipe-${recipe.recipe_id}`}
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      {t('baby.adapt_recipe')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Adapt Recipe Dialog */}
        <Dialog open={adaptDialog.open} onOpenChange={(open) => !open && setAdaptDialog({ open: false, recipe: null })}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Baby className="w-5 h-5 text-pink-500" />
                {t('baby.adapted_recipe')}: {adaptDialog.recipe?.title}
              </DialogTitle>
            </DialogHeader>
            
            {adapting ? (
              <div className="py-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4" />
                <p className="text-gray-600">{t('baby.adapting')}</p>
              </div>
            ) : adaptedRecipe ? (
              <div className="space-y-4 py-4">
                <div>
                  <h4 className="font-semibold mb-2">{adaptedRecipe.title || t('baby.adapted_version')}</h4>
                  {adaptedRecipe.description && <p className="text-sm text-gray-600">{adaptedRecipe.description}</p>}
                </div>

                {adaptedRecipe.not_suitable_ingredients?.length > 0 && (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <h5 className="font-medium text-red-700 mb-2">{t('baby.removed_ingredients')}</h5>
                    <ul className="text-sm text-red-600 space-y-1">
                      {adaptedRecipe.not_suitable_ingredients.map((item, idx) => (
                        <li key={idx}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {adaptedRecipe.ingredients?.length > 0 && (
                  <div>
                    <h5 className="font-medium mb-2">{t('recipe.ingredients')}</h5>
                    <ul className="text-sm space-y-1">
                      {adaptedRecipe.ingredients.map((ing, idx) => (
                        <li key={idx}>• {ing.amount} {ing.unit} {ing.name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {adaptedRecipe.steps?.length > 0 && (
                  <div>
                    <h5 className="font-medium mb-2">{t('recipe.instructions')}</h5>
                    <ol className="text-sm space-y-2">
                      {adaptedRecipe.steps.map((step, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span className="flex-shrink-0 w-6 h-6 bg-pink-500 text-white rounded-full flex items-center justify-center text-xs">{idx + 1}</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {adaptedRecipe.tips?.length > 0 && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <h5 className="font-medium text-green-700 mb-2">{t('baby.safety_tips')}</h5>
                    <ul className="text-sm text-green-600 space-y-1">
                      {adaptedRecipe.tips.map((tip, idx) => (
                        <li key={idx}>• {tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={() => setAdaptDialog({ open: false, recipe: null })}>
                {t('common.close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default BabyModePage;
